import React, { useEffect, useMemo, useState } from 'react';
import useShots from '../use-shots';
import useCanvasConfig from '../use-canvas-config';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { ShotFeed, Shot } from '../../stores/reducers/shots';
import { sleep } from '../../utils/utils';

type CoordsMap = Map<number, {R: co, L: co}>

const graphicElementId = (
	id: string | number,
	isForShotEditorModal: boolean,
): string => `custom-graphic-vid-${id}${isForShotEditorModal ? '-shot-editor' : ''}`

const mainVideoElementId = (
	isPip: boolean,
	isForShotEditorModal: boolean
) => {
	let id = 'main-video-view-current';
	if (isPip) id = id + '-pip';
	if (isForShotEditorModal) id = id + '-modal';
	return id;
}

// R1 = Bottom Right corner of Rect1
// L2 = Top left corner of Rect2
interface co {x: number, y: number}
const calcOverlap = (R1:co, R2:co, L1:co, L2:co) => {
	const x_distance = Math.min(R1.x, R2.x) - Math.max(L1.x, L2.x)
	const y_distance = Math.min(R1.y, R2.y) - Math.max(L1.y, L2.y)
	let area = 0;
	if (x_distance > 0 && y_distance > 0)
		area = x_distance * y_distance;
	return area;
}

const getLRGivenEl = (el: Element | null) => {
	if (!el) return {L:{x:0,y:0},R:{x:0,y:0}}
	const boundingBox = el.getBoundingClientRect();
	const L = {
		x: Math.trunc(boundingBox.x),
		y: Math.trunc(boundingBox.y)
	}
	const R = {
		x: Math.trunc(boundingBox.x + boundingBox.width),
		y: Math.trunc(boundingBox.y + boundingBox.height)
	}
	return {L, R};
}

const nonTouchingOverlap = (
	graphicStart: number,
	graphicEnd: number,
	feedStart: number,
	feedEnd: number
) => Math.max(0, Math.min(graphicEnd, feedEnd) - Math.max(graphicStart, feedStart));


const delayFindGraphic = async (id: string) => {
	// ran into an issue where on first drop the hook runs too early,
	// before the element exists. This will make the check run after the
	// render where the element exists.
	let el = document.getElementById(id);
	if (!el) {
		await sleep(30);
		el = document.getElementById(id);
	}
	return el;
}


function getVideoCoords(isForShotEditorModal: boolean) {
	const mainVideoEl = document.getElementById(mainVideoElementId(false, isForShotEditorModal));
	const allVidCoords: CoordsMap = new Map();
	if (mainVideoEl) {
		const mainVideoElPip = document.getElementById(mainVideoElementId(true, isForShotEditorModal));
		if (mainVideoElPip) {
			allVidCoords.set(0, getLRGivenEl((mainVideoElPip.children.item(0))))
		}

		let i = 0;
		const len = mainVideoEl?.children.length ?? 0;
		for (i; i<len; i+=1) {
			const trueIndex = mainVideoElPip ? i + 1 : i;
			allVidCoords.set(trueIndex, getLRGivenEl(mainVideoEl.children.item(i)));
		}
	}
	
	return allVidCoords;
}

export function graphicIdByPresenterFeedIndex(
	currentShot: Shot,
	isForShotEditorModal: boolean = false,
): Record<number, number | null> {
	const result: Record<number, number | null> = {};
	if (!currentShot?.shot_data?.shotGraphics || !currentShot.shot_data.shotFeeds) {
		return {};
	}
	const graphicIds = currentShot.shot_data.shotGraphics;
	const shotFeeds = currentShot.shot_data.shotFeeds?.filter((shotFeed) => !shotFeed.audioOnly) ?? [];;
	const getVideoCoords = () => {
		const mainVideoEl = document.getElementById(mainVideoElementId(false, isForShotEditorModal));
		const coordsMap: Map<number, any> = new Map();
		const len = mainVideoEl?.children.length ?? 0;

		if (mainVideoEl) {
			for (let i = 0; i < len; i++) {
				const child = mainVideoEl.children.item(i);
				coordsMap.set(i, getLRGivenEl(child));
			}
		}

		return coordsMap;
	};

	const getBestFeedIndex = (graphicId: number): number | null => {
		let maxOverlap = 0;
		let bestIndex: number | null = null;

		const graphicEl = document.getElementById(graphicElementId(graphicId, isForShotEditorModal));
		if (!graphicEl) return null;

		const gCoords = getLRGivenEl(graphicEl);
		const videoCoords = getVideoCoords();

		for (const [vidIndex, vidCords] of videoCoords) {
			const overlap = calcOverlap(vidCords.R, gCoords.R, vidCords.L, gCoords.L);
			if (overlap > maxOverlap) {
				maxOverlap = overlap;
				bestIndex = vidIndex;
			}
		}

		return bestIndex;
	};

	for (const id of graphicIds) {
		const bestFeedIndex = getBestFeedIndex(id);
		result[id] = bestFeedIndex !== null ? shotFeeds[bestFeedIndex]?.presenter ?? null : null;
	}

	return result;
}

export function useDragDetectFeed(
	graphicId: number,
	isForShotEditorModal: boolean = false,
) {
	// shot feeds
	const { currentShot } = useShots();
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const { shotFeeds: canvasShotFeeds } = useCanvasConfig(isForShotEditorModal ? shotModalShot : currentShot);
	const shotFeeds = useMemo(() => canvasShotFeeds?.filter((shotFeed) => !shotFeed.audioOnly) ?? [], [canvasShotFeeds]);

	// graphics
	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const shotModalGraphics = useSelector((s: GS) => s.GraphicsReducer.shotModalGraphics);
	const currentGraphics = useMemo(() => isForShotEditorModal ? shotModalGraphics : graphics, [isForShotEditorModal, shotModalGraphics, graphics]);

	const [feedPresenter, setFeedPresenter] = useState<number|null>(null);

	const graphicItem = useMemo(() => currentGraphics.find((g) => g.id === graphicId), [currentGraphics, graphicId])
	const memoizedPosition = useMemo(() => {
		if (!graphicItem) return 0;
		const {pos_x = 0, pos_y = 0, scale_x = 0, scale_y = 0, rotate = 0} = graphicItem;
		return `${pos_x} + ${pos_y} + ${scale_x} + ${scale_y} + ${rotate}`
	}, [graphicItem?.pos_x, graphicItem?.pos_y, graphicItem?.scale_x, graphicItem?.scale_y])

	const getBiggestFeedIndex = async (id: number, shotFeeds: ShotFeed[]) => {
		let biggest: number = 0;
		let biggestFeedIndex: number | null = null;

		const gCords = getLRGivenEl(await delayFindGraphic(graphicElementId(id, isForShotEditorModal)));

		const videoCoords = getVideoCoords(isForShotEditorModal);
		const vidCoordsArr = Array.from(videoCoords);
		for (const [vidIndex, vidCords] of vidCoordsArr) {
			if (shotFeeds[vidIndex]?.presenter) {
				const overlap = calcOverlap(vidCords.R, gCords.R, vidCords.L, gCords.L);
				
				if (overlap > biggest) {
					biggest = overlap;
					biggestFeedIndex = vidIndex;
				}
			}
		}

		if (biggestFeedIndex === null) {
			for (const [vidIndex, vidCords] of vidCoordsArr) {
				if (shotFeeds[vidIndex]?.presenter) {
					const overlap = nonTouchingOverlap(gCords.L.y, gCords.R.y, vidCords.L.y, vidCords.R.y);
					if (overlap > biggest) {
						biggest = overlap;
						biggestFeedIndex = vidIndex;
					}
				}
			}
		}

		if (biggestFeedIndex === null) {
			for (const [vidIndex, vidCords] of vidCoordsArr) {
				if (shotFeeds[vidIndex]?.presenter) {
					const overlap = nonTouchingOverlap(gCords.L.x, gCords.R.x, vidCords.L.x, vidCords.R.x);
					if (overlap > biggest) {
						biggest = overlap;
						biggestFeedIndex = vidIndex;
					}
				}
			}
		}

		// make sure 0 is a valid value ONLY null returns null!
		setFeedPresenter(biggestFeedIndex === null ? null : shotFeeds[biggestFeedIndex]?.presenter ?? null);
	}
	
	useEffect(() => {
		if (graphicId) {
			getBiggestFeedIndex(graphicId, shotFeeds);
		}
	}, [graphicId, shotFeeds, memoizedPosition])

	return {feedPresenter}
}